import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Flag, ArrowRight, ArrowLeft, CheckCircle, Phone, Loader2, Car
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const STEPS = ['Driver Profile', 'Business Position', 'Vision & Goals', 'ACCD Readiness', 'Commitment'];

const TrackDIntake = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    // Step 1
    fullName: '', preferredName: '', phone: '', email: '', cityState: '',
    bestTime: '', howFound: '', referredBy: '',
    operationType: [], yearsDriving: '', weeklyTrips: '', monthlyGross: '', monthlyNet: '',
    vehicleYMM: '', vehicleOwnership: '', vehiclePayment: '', vehicleStandard: '',
    // Step 2
    hasEntity: '', einEstablished: '', hasBankAccount: '', financesSeparated: '',
    businessCreditStatus: '', personalCreditRange: '', capitalToInvest: '', readyToInvest: '',
    // Step 3
    bigPicture: '', transitionTimeline: '', serviceFocus: [],
    targetCity: '', idealClient: '', firstHirePlan: '',
    // Step 4
    knowledgeEntity: 3, knowledgeCredit: 3, knowledgePricing: 3, knowledgeCorporate: 3,
    knowledgeDrivers: 3, knowledgeFleet: 3, knowledgeService: 3, knowledgeMarketing: 3,
    previousAttempts: '', previousDetails: '',
    // Step 5
    commitProgram: 3, commitFinancial: 3, commitTransition: 3, commitCoachable: 3, commitUrgency: 3,
    engagementPref: '', whyAce: '', anythingElse: '',
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleArr = (k, v) => setForm(p => {
    const a = p[k] || [];
    return { ...p, [k]: a.includes(v) ? a.filter(x => x !== v) : [...a, v] };
  });

  const handleSubmit = async () => {
    if (!form.fullName || !form.phone || !form.email) return;
    setSubmitting(true);
    try {
      await fetch(`${API}/api/consulting/intake`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, route: 'accelerator', formType: 'track_d', submittedAt: new Date().toISOString() })
      });
      setSubmitted(true);
    } catch (e) { console.error(e); }
    setSubmitting(false);
  };

  if (submitted) return (
    <div className="min-h-screen bg-black flex items-center justify-center p-6" data-testid="trackd-success">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 rounded-full bg-red-600/20 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="h-8 w-8 text-red-500" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Assessment Received.</h2>
        <p className="text-gray-400 text-sm mb-6">Your ACE consultant will review your Track D intake personally and reach out within 24 hours to confirm your Discovery Session. Come ready to build.</p>
        <p className="text-xs text-gray-600 mb-8">Next Step: Paid Discovery Session — 60 min with Your Auto Concierge</p>
        <div className="flex gap-3 justify-center">
          <Button onClick={() => navigate('/driving')} variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-900 text-xs">Back to Consulting</Button>
          <a href="tel:6788463009" className="inline-flex items-center h-9 px-4 rounded-md bg-red-600 hover:bg-red-700 text-white text-xs font-bold"><Phone className="h-3.5 w-3.5 mr-1.5" /> (678) 846-3009</a>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white" data-testid="track-d-intake-page">
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-950">
        <div className="max-w-3xl mx-auto px-6 py-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center">
              <Flag className="h-4 w-4 text-white" />
            </div>
            <div>
              <p className="text-red-500 text-[10px] font-bold tracking-[0.3em] uppercase">The Accelerator — Route D</p>
              <h1 className="text-lg font-bold">Driver-to-Owner-Operator Assessment</h1>
            </div>
          </div>
          <p className="text-sm text-gray-500">Complete this before your Discovery Session with ACE. Honest answers only — this takes about 6 minutes.</p>
        </div>
      </div>

      {/* Progress */}
      <div className="border-b border-gray-900 bg-gray-950/50">
        <div className="max-w-3xl mx-auto px-6 py-3">
          <div className="flex items-center gap-1">
            {STEPS.map((s, i) => (
              <React.Fragment key={i}>
                <div className={`flex items-center gap-1.5 ${step > i + 1 ? 'text-green-500' : step === i + 1 ? 'text-white' : 'text-gray-700'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                    step > i + 1 ? 'bg-green-500/20 text-green-500' : step === i + 1 ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-600'
                  }`}>{step > i + 1 ? <CheckCircle className="h-3.5 w-3.5" /> : i + 1}</div>
                  <span className="text-[10px] font-semibold hidden sm:inline">{s}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-1 ${step > i + 1 ? 'bg-green-500/30' : 'bg-gray-800'}`} />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10">
        {/* Step 1 — Driver Profile */}
        {step === 1 && (
          <div className="space-y-8" data-testid="step-driver-profile">
            <div><h2 className="text-lg font-bold mb-1">Who You Are & How You Operate</h2></div>
            <Sec title="Personal Information">
              <F label="Full Name *" v={form.fullName} set={v => set('fullName', v)} tid="field-fullName" />
              <F label="Preferred Name" v={form.preferredName} set={v => set('preferredName', v)} />
              <F label="Phone *" v={form.phone} set={v => set('phone', v)} tid="field-phone" />
              <F label="Email *" v={form.email} set={v => set('email', v)} tid="field-email" />
              <F label="City / State of Operation" v={form.cityState} set={v => set('cityState', v)} />
              <Sel label="Best Time to Reach" v={form.bestTime} set={v => set('bestTime', v)} opts={['Morning 8am–12pm', 'Afternoon 12pm–5pm', 'Evening 5pm–9pm', 'Anytime']} />
              <Sel label="How did you find ACE / Track D?" v={form.howFound} set={v => set('howFound', v)} opts={['Text / Direct Outreach', 'Instagram', 'Facebook', 'Referral', 'Driven or Driving Podcast', 'Google / Web Search', 'YourAutoConcierge.com', 'Other']} />
              <F label="Referred by (if applicable)" v={form.referredBy} set={v => set('referredBy', v)} />
            </Sec>
            <Sec title="How You Currently Drive">
              <Checks sel={form.operationType} toggle={v => toggleArr('operationType', v)} opts={['Uber / Lyft', 'Black Car Company', 'Independent — own clients', 'Delivery / Courier', 'Part-time — have another job', 'Not currently driving']} />
              <Sel label="Years driving professionally" v={form.yearsDriving} set={v => set('yearsDriving', v)} opts={['Less than 1 year', '1–2 years', '2–5 years', '5–10 years', '10+ years']} />
              <F label="Trips or hours per week (approx)" v={form.weeklyTrips} set={v => set('weeklyTrips', v)} />
              <F label="Monthly gross earnings" v={form.monthlyGross} set={v => set('monthlyGross', v)} />
              <F label="Monthly net after expenses" v={form.monthlyNet} set={v => set('monthlyNet', v)} />
            </Sec>
            <Sec title="Your Vehicle">
              <F label="Year / Make / Model" v={form.vehicleYMM} set={v => set('vehicleYMM', v)} />
              <Sel label="Owned, Financed, or Leased?" v={form.vehicleOwnership} set={v => set('vehicleOwnership', v)} opts={['Owned outright', 'Financed', 'Leased', 'Other']} />
              <F label="Monthly payment (if any)" v={form.vehiclePayment} set={v => set('vehiclePayment', v)} />
              <Radios v={form.vehicleStandard} set={v => set('vehicleStandard', v)} opts={['Yes — client-ready today', 'Mostly — minor upgrades needed', 'No — needs significant work', 'Planning to acquire a different vehicle']} />
            </Sec>
          </div>
        )}

        {/* Step 2 — Business Position */}
        {step === 2 && (
          <div className="space-y-8" data-testid="step-business-position">
            <div><h2 className="text-lg font-bold mb-1">Your Business & Credit Position</h2><p className="text-sm text-gray-500">No judgment — we need to know where the foundation stands.</p></div>
            <Sec title="Business Structure">
              <Radios v={form.hasEntity} set={v => set('hasEntity', v)} opts={['Yes — LLC', 'Yes — Corporation', 'Yes — Sole Proprietor', 'No entity — operating personally']} />
              <Sel label="EIN established?" v={form.einEstablished} set={v => set('einEstablished', v)} opts={['Yes', 'No', 'Not sure']} />
              <Sel label="Business bank account?" v={form.hasBankAccount} set={v => set('hasBankAccount', v)} opts={['Yes', 'No']} />
              <Radios v={form.financesSeparated} set={v => set('financesSeparated', v)} opts={['Yes — fully separate', 'Partially — working on it', 'No — everything is mixed', 'Not sure']} />
            </Sec>
            <Sec title="Business Credit Status">
              <Radios v={form.businessCreditStatus} set={v => set('businessCreditStatus', v)} opts={['No profile yet — starting from zero', 'DUNS number established only', 'A few tradelines open', 'Multiple tradelines — strong score in progress', 'Established — 70+ Paydex score']} />
            </Sec>
            <Sec title="Personal Credit & Capital">
              <Radios v={form.personalCreditRange} set={v => set('personalCreditRange', v)} opts={['Below 580', '580–649', '650–699', '700–749', '750+', 'Not sure']} />
              <Radios v={form.capitalToInvest} set={v => set('capitalToInvest', v)} opts={['Under $1,000', '$1,000–$5,000', '$5,000–$15,000', '$15,000+', 'Seeking financing options']} />
              <Radios v={form.readyToInvest} set={v => set('readyToInvest', v)} opts={['Yes — ready now', 'Almost — within 30 days', 'Need 60–90 days', 'Need financing or payment plan']} />
            </Sec>
          </div>
        )}

        {/* Step 3 — Vision & Goals */}
        {step === 3 && (
          <div className="space-y-8" data-testid="step-vision-goals">
            <div><h2 className="text-lg font-bold mb-1">Where Are You Going?</h2><p className="text-sm text-gray-500">The more specific, the better roadmap we build.</p></div>
            <Sec title="The Big Picture">
              <div className="sm:col-span-2">
                <Textarea value={form.bigPicture} onChange={e => set('bigPicture', e.target.value)} className="bg-gray-900 border-gray-700 text-white min-h-[100px]" placeholder="Describe your operation 12 months from now — what does a successful week look like?" />
              </div>
            </Sec>
            <Sec title="Transition Timeline">
              <Radios v={form.transitionTimeline} set={v => set('transitionTimeline', v)} opts={['Already independent — looking to scale', 'Within 30 days', '1–3 months', '3–6 months', '6–12 months', 'Still planning — no hard date']} />
            </Sec>
            <Sec title="Service Focus">
              <Checks sel={form.serviceFocus} toggle={v => toggleArr('serviceFocus', v)} opts={['Luxury / Black Car', 'Airport Transfers', 'Corporate Accounts', 'Event & Entertainment', 'Executive / VIP', 'Multi-vehicle / Fleet', 'Broker / Referral Model']} />
            </Sec>
            <Sec title="Target Market">
              <F label="Primary city / market" v={form.targetCity} set={v => set('targetCity', v)} />
              <F label="Who is your ideal client?" v={form.idealClient} set={v => set('idealClient', v)} />
            </Sec>
            <Sec title="First Hire">
              <Radios v={form.firstHirePlan} set={v => set('firstHirePlan', v)} opts={['Already have someone in mind', 'Within 6 months', 'After 3+ regular clients', '12+ months — solo first', 'Not sure yet']} />
            </Sec>
          </div>
        )}

        {/* Step 4 — ACCD Readiness */}
        {step === 4 && (
          <div className="space-y-8" data-testid="step-accd-readiness">
            <div><h2 className="text-lg font-bold mb-1">Knowledge Self-Assessment</h2><p className="text-sm text-gray-500">Rate your honest current knowledge in each area (1–5). This tells us where to focus.</p></div>
            <div className="space-y-5">
              {[
                ['knowledgeEntity', 'Business entity structure & legal setup'],
                ['knowledgeCredit', 'Building & managing business credit'],
                ['knowledgePricing', 'Pricing transportation services profitably'],
                ['knowledgeCorporate', 'Landing & managing corporate accounts'],
                ['knowledgeDrivers', 'Hiring & managing IC drivers'],
                ['knowledgeFleet', 'Fleet acquisition & vehicle financing'],
                ['knowledgeService', 'Luxury / concierge service standards'],
                ['knowledgeMarketing', 'Marketing & client acquisition'],
              ].map(([key, label]) => (
                <RatingRow key={key} label={label} value={form[key]} onChange={v => set(key, v)} />
              ))}
            </div>
            <Sec title="Previous Attempts">
              <Radios v={form.previousAttempts} set={v => set('previousAttempts', v)} opts={['No — first attempt', 'Yes — attempted but stalled', 'Yes — had something, then stopped']} />
              <div className="sm:col-span-2">
                <Textarea value={form.previousDetails} onChange={e => set('previousDetails', e.target.value)} className="bg-gray-900 border-gray-700 text-white min-h-[80px]" placeholder="If yes — what happened? What stopped you?" />
              </div>
            </Sec>
          </div>
        )}

        {/* Step 5 — Commitment */}
        {step === 5 && (
          <div className="space-y-8" data-testid="step-commitment">
            <div><h2 className="text-lg font-bold mb-1">Are You Ready to Build?</h2><p className="text-sm text-gray-500">This is where we assess fit and how you want to work with ACE.</p></div>
            <div className="space-y-5">
              {[
                ['commitProgram', 'Commitment to completing ACCD program'],
                ['commitFinancial', 'Willingness to invest financially'],
                ['commitTransition', 'Readiness to transition from driver to operator'],
                ['commitCoachable', 'Coachability — ability to follow a proven system'],
                ['commitUrgency', 'Urgency — how important is this transition'],
              ].map(([key, label]) => (
                <RatingRow key={key} label={label} value={form[key]} onChange={v => set(key, v)} />
              ))}
            </div>
            <Sec title="Engagement Preference">
              <Radios v={form.engagementPref} set={v => set('engagementPref', v)} opts={[
                'ACCD Bundle — certification + roadmap, then execute solo',
                'ACCD + Condo Retainer — ongoing support monthly',
                'ACCD + Mentorship — ACE embedded in my build',
                "Not sure — let's figure it out",
              ]} />
            </Sec>
            <Sec title="Why ACE?">
              <div className="sm:col-span-2">
                <Textarea value={form.whyAce} onChange={e => set('whyAce', e.target.value)} className="bg-gray-900 border-gray-700 text-white min-h-[80px]" placeholder="Why ACE? Why now?" data-testid="field-whyAce" />
              </div>
              <div className="sm:col-span-2">
                <Textarea value={form.anythingElse} onChange={e => set('anythingElse', e.target.value)} className="bg-gray-900 border-gray-700 text-white min-h-[60px]" placeholder="Anything else we should know?" />
              </div>
            </Sec>
            <p className="text-[11px] text-gray-600">By submitting, you confirm the information is accurate and you're genuinely interested in the ACE Track D / ACCD Certification program. All information is confidential.</p>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-10 pt-6 border-t border-gray-800">
          {step > 1 ? (
            <Button onClick={() => setStep(s => s - 1)} variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-900 text-xs" data-testid="btn-back">
              <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Back
            </Button>
          ) : <div />}
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-gray-600">Step {step} of 5</span>
            {step < 5 ? (
              <Button onClick={() => setStep(s => s + 1)} className="bg-red-600 hover:bg-red-700 text-xs" data-testid="btn-continue"
                disabled={step === 1 && (!form.fullName || !form.phone || !form.email)}>
                Continue <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} className="bg-red-600 hover:bg-red-700 text-xs" disabled={submitting} data-testid="btn-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                Submit Assessment
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══ Reusable Sub-Components ═══ */
const F = ({ label, v, set, tid }) => (
  <div>
    <label className="text-xs font-medium text-gray-400 mb-1.5 block">{label}</label>
    <Input value={v} onChange={e => set(e.target.value)} className="bg-gray-900 border-gray-700 text-white text-sm h-10" data-testid={tid} />
  </div>
);
const Sel = ({ label, v, set, opts }) => (
  <div>
    <label className="text-xs font-medium text-gray-400 mb-1.5 block">{label}</label>
    <select value={v} onChange={e => set(e.target.value)} className="w-full h-10 px-3 rounded-md bg-gray-900 border border-gray-700 text-white text-sm">
      <option value="">Select...</option>
      {opts.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);
const Sec = ({ title, children }) => (
  <div>
    <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-4">{title}</p>
    <div className="grid sm:grid-cols-2 gap-4">{children}</div>
  </div>
);
const Radios = ({ v, set, opts }) => (
  <div className="sm:col-span-2 grid gap-2">
    {opts.map(o => (
      <button key={o} onClick={() => set(o)} type="button" className={`w-full text-left px-4 py-3 rounded-lg border text-sm transition-all ${v === o ? 'border-red-600 bg-red-600/10 text-white' : 'border-gray-800 text-gray-400 hover:border-gray-700'}`}>
        <div className="flex items-center gap-3">
          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${v === o ? 'border-red-500' : 'border-gray-700'}`}>
            {v === o && <div className="w-2 h-2 rounded-full bg-red-500" />}
          </div>
          {o}
        </div>
      </button>
    ))}
  </div>
);
const Checks = ({ sel, toggle, opts }) => (
  <div className="sm:col-span-2 grid sm:grid-cols-2 gap-2">
    {opts.map(o => (
      <button key={o} onClick={() => toggle(o)} type="button" className={`text-left px-4 py-3 rounded-lg border text-sm transition-all ${sel.includes(o) ? 'border-red-600 bg-red-600/10 text-white' : 'border-gray-800 text-gray-400 hover:border-gray-700'}`}>
        <div className="flex items-center gap-3">
          <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${sel.includes(o) ? 'bg-red-600 border-red-600' : 'border-gray-700'}`}>
            {sel.includes(o) && <CheckCircle className="h-3 w-3 text-white" />}
          </div>
          {o}
        </div>
      </button>
    ))}
  </div>
);
const RatingRow = ({ label, value, onChange }) => (
  <div className="flex items-center justify-between gap-4 p-3 rounded-lg border border-gray-800 bg-gray-950">
    <p className="text-sm text-gray-300 flex-1">{label}</p>
    <div className="flex gap-1.5">
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} onClick={() => onChange(n)} type="button"
          className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${
            value === n ? 'bg-red-600 text-white' : value > n ? 'bg-red-600/20 text-red-400' : 'bg-gray-800 text-gray-600 hover:bg-gray-700'
          }`}>{n}</button>
      ))}
    </div>
  </div>
);

export default TrackDIntake;
